import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import MetaTags from 'react-meta-tags';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import SubCatCard from '../components/SubcatCard';
import '../styles/subcategorypage.css';

export default function SubCategoryPage() {
  const { slug: slugParam, catName } = useParams();
  const slug = String(slugParam ?? catName ?? '').trim();
  const navigate = useNavigate();
  const location = useLocation();

  const [subCategories, setSubCategories] = useState([]);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);

  // NEW: states for free-credit flow
  const [freeCredits, setFreeCredits] = useState(0);
  const [showFreeConfirm, setShowFreeConfirm] = useState(false);
  const [isFetchingCredits, setIsFetchingCredits] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState(null);

  const isLoggedIn = (() => {
    try {
      const raw = localStorage.getItem('isLogin');
      const u = raw ? JSON.parse(raw) : null;
      return !!(u && u.expiry && u.expiry > Date.now());
    } catch {
      return false;
    }
  })();

  const goLogin = (intent = 'view') => {
    navigate('/login', { state: { redirectAfterLogin: location.pathname, intent } });
  };

  const getLoggedInUser = () => {
    try {
      const raw = localStorage.getItem('isLogin');
      const u = raw ? JSON.parse(raw) : null;
      if (u && u.expiry && u.expiry > Date.now()) return u;
    } catch (_) {}
    return null;
  };

  // ===== Load JSON category info =====
  useEffect(() => {
    if (!slug) return;
    async function loadJson() {
      try {
        const mod = await import(`../data/${slug}.json`);
        setCategoryInfo(mod.default);
        setNotFound(false);
      } catch (err) {
        console.warn(`No JSON file found for ${slug}`);
        setCategoryInfo(null);
      } finally {
        window.scrollTo(0, 0);
      }
    }
    loadJson();
  }, [slug]);

  // ===== Load subcategories from API =====
  const getAllSubCategories = async () => {
    
    try {
      const json = JSON.stringify({ id: slug.replaceAll('-', ' ') });
      const headers = { 'Content-Type': 'application/json' };

      const response = await axios.post(
        'https://epolylearning.com/Services/polyquiz.svc/Category',
        json,
        { headers }
      );

      const subCatData = response.data?.CategoryResult?.SubCategoriesTypes || [];
      setSubCategories(subCatData);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    }
  };

  useEffect(() => {
    if (slug) getAllSubCategories();
  }, [slug]);




    // ===== Check subscription =====
   // ===== Check subscription =====
useEffect(() => {
  const check = async () => {
    if (!slug || !isLoggedIn) {
      setIsSubscribed(false);
      return;
    }

    setIsCheckingAccess(true);
    try {
      const user = getLoggedInUser();
      if (!user || !user.userid) {
        setIsSubscribed(false);
        return;
      }

      const payload = JSON.stringify({
        userId: String(user.userid),
        category: slug.replaceAll('-', ' ')
      });

      const headers = { 'Content-Type': 'application/json' };

      const res = await axios.post(
        'https://epolylearning.com/Services/polyquiz.svc/GetSubscriptionData',
        payload,
        { headers }
      );

      // Normalize possible WCF/ASMX wrapping and multiple shapes
      let raw = res?.data;

      // if service returned wrapped result like { GetSubscriptionDataResult: '...'}
      if (raw && typeof raw === 'object' && raw.GetSubscriptionDataResult) {
        raw = raw.GetSubscriptionDataResult;
      }

      // raw might be a JSON string or an object or a boolean
      let parsed;
      if (typeof raw === 'string') {
        try {
          parsed = JSON.parse(raw);
        } catch (e) {
          // maybe it's "true"/"false" or plain boolean string
          const lowered = raw.trim().toLowerCase();
          if (lowered === 'true' || lowered === 'false') {
            parsed = { IsSubscribed: lowered === 'true' };
          } else {
            parsed = raw;
          }
        }
      } else if (typeof raw === 'boolean') {
        parsed = { IsSubscribed: raw };
      } else {
        parsed = raw;
      }

      // Accept several property names
      const isSubscribed =
        (parsed && (parsed.IsSubscribed ?? parsed.Issubscribed ?? parsed.issubscribed ?? parsed.issubcribed)) ??
        (typeof parsed === 'boolean' ? parsed : false);

      setIsSubscribed(Boolean(isSubscribed));
    } catch (e) {
      console.warn('Subscription check failed:', e);
      setIsSubscribed(false);
    } finally {
      setIsCheckingAccess(false);
    }
  };

  check();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [slug, isLoggedIn]);


  // ===== Gatekeeper =====
  const ensureAccess = async () => {
    if (!isLoggedIn) {
      navigate('/login', { state: { redirectAfterLogin: `/practice-test-exam-prep/${slug}` } });
      return false;
    }
    if (!isSubscribed) {
      navigate(`/subscribe-practice-test/${slug}`, {
        state: { categoryName: slug.replaceAll('-', ' ') },
      });
      return false;
    }
    return true;
  };

  // ===== New: helpers for free-credit check + redeem =====

  // Call backend to fetch the current free credit count for logged in user
 // Call backend to fetch the current free credit count for logged in user
const fetchFreeCredits = async () => {
  try {
    const user = getLoggedInUser();
    if (!user || !user.userid) {
      setFreeCredits(0);
      return 0;
    }

    const payload = JSON.stringify({ userId: user.userid });

    const res = await axios.post(
      'https://epolylearning.com/Services/polyquiz.svc/GetUserFreeCredits',
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    );

    // res.data can be many shapes. normalize it safely
    let raw = res.data;

    // If WCF/ASMX wrapped the response under GetUserFreeCreditsResult, use it
    if (raw && typeof raw === 'object' && raw.GetUserFreeCreditsResult) {
      raw = raw.GetUserFreeCreditsResult;
    }

    // If raw is a JSON string (server serialized payload), parse it
    let parsed;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        // raw might be a simple number encoded as a string: e.g. "2"
        const num = parseInt(raw.replace(/[^0-9\-]/g, ''), 10);
        const finalNum = Number.isFinite(num) ? num : 0;
        setFreeCredits(finalNum);
        return finalNum;
      }
    } else if (typeof raw === 'number') {
      // server returned a raw number
      setFreeCredits(raw);
      return raw;
    } else {
      parsed = raw; // already an object
    }

    // Now parsed should be an object { FreeTestCredits, Message, Success } or similar
    const credits =
      (parsed && (parsed.FreeTestCredits ?? parsed.freeTestCredits)) ??
      (parsed && parsed) ??
      0;

    const numeric = Number(credits) || 0;
    setFreeCredits(numeric);
    return numeric;
  } catch (error) {
    console.error("Error fetching free credits:", error);
    setFreeCredits(0);
    return 0;
  }
};



  // PRE-FETCH free credits on page load (and when slug or login status changes)
  useEffect(() => {
    if (isLoggedIn) {
      fetchFreeCredits().catch((e) => {
        console.warn('prefetch free credits error', e);
      });
    } else {
      setFreeCredits(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isLoggedIn]);

  // Redeem one free credit and create a server-side allowance to start the test
const redeemOneFreeCredit = async (testId = null) => {
  const user = getLoggedInUser();
  if (!user) return { success: false, message: 'Not logged in' };
  setIsRedeeming(true);
  setRedeemError(null);

  try {
    const payload = JSON.stringify({
      userId: user.userid,
      category: slug.replaceAll('-', ' '),
      testId
    });
    const headers = { 'Content-Type': 'application/json' };

    const res = await axios.post(
      'https://epolylearning.com/Services/polyquiz.svc/RedeemFreeCredit',
      payload,
      { headers }
    );

    // Normalize server response:
    // possible shapes:
    // 1) res.data = { RedeemFreeCreditResult: '{"Remaining":1,...}' }
    // 2) res.data = '{"Remaining":1,...}'   (string)
    // 3) res.data = { Remaining:1, Message:'OK', Success:true }  (object)
    let raw = res?.data;
    if (raw && typeof raw === 'object' && raw.RedeemFreeCreditResult) {
      raw = raw.RedeemFreeCreditResult;
    }

    let parsed;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        // fallback: maybe server returned a plain number as string
        const num = parseInt(raw.replace(/[^0-9\-]/g, ''), 10);
        const remainingFallback = Number.isFinite(num) ? num : null;
        if (remainingFallback !== null) {
          const remaining = remainingFallback;
          setFreeCredits(isNaN(remaining) ? Math.max(0, freeCredits - 1) : remaining);
          // update localStorage copy if present
          try {
            const rawLs = localStorage.getItem('isLogin');
            if (rawLs) {
              const parsedLs = JSON.parse(rawLs);
              parsedLs.freeTestCredits = remaining;
              localStorage.setItem('isLogin', JSON.stringify(parsedLs));
            }
          } catch (_) {}
          return { success: true, remaining };
        } else {
          setRedeemError('Invalid server response');
          return { success: false, message: 'Invalid server response' };
        }
      }
    } else if (typeof raw === 'number') {
      parsed = { Remaining: raw, Message: 'OK', Success: true };
    } else {
      parsed = raw; // already an object
    }

    // parsed should now be an object
    const successFlag = parsed && (parsed.Success === true || parsed.success === true);
    const remaining = Number(parsed?.Remaining ?? parsed?.remaining ?? 0);

    if (successFlag) {
      setFreeCredits(isNaN(remaining) ? Math.max(0, freeCredits - 1) : remaining);

      // update localStorage copy if present
      try {
        const rawLs = localStorage.getItem('isLogin');
        if (rawLs) {
          const parsedLs = JSON.parse(rawLs);
          parsedLs.freeTestCredits = remaining;
          localStorage.setItem('isLogin', JSON.stringify(parsedLs));
        }
      } catch (_) {}

      return { success: true, remaining };
    } else {
      const msg = parsed?.Message || parsed?.message || 'Unable to redeem';
      setRedeemError(msg);
      return { success: false, message: msg };
    }
  } catch (e) {
    console.error('Redeem error:', e);
    setRedeemError('Server error while redeeming credit.');
    return { success: false, message: 'Server error' };
  } finally {
    setIsRedeeming(false);
  }
};


  // ===== CTA handlers =====
  const startFull = async () => {
    // if somehow not logged in, go to login (keeps your intent)
    if (!isLoggedIn) {
      navigate('/login', { state: { redirectAfterLogin: location.pathname, intent: 'subscribe' } });
      return;
    }

    // If user has prefetched free credits > 0, show confirmation to use one (no subscription required)
    const creditsNow = Number(freeCredits) || 0;
    if (creditsNow > 0 && !isSubscribed) {
      setShowFreeConfirm(true);
      return;
    }

    // No free credits: preserve existing behavior
    if (!isSubscribed) {
      const pricingEl = document.getElementById('pricing-section');
      if (pricingEl) {
        pricingEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        navigate(`/subscribe-practice-test/${slug}`, {
          state: { categoryName: slug.replaceAll('-', ' ') },
        });
      }
      return;
    }

    // Subscribed → start test
    navigate(`/online-practice-test/${slug}`, { state: { type: 'category-test', quesNumber: categoryInfo.examOutline.totalQuestions} });
  };

  // Called when user confirms to use a free credit
  const confirmUseFree = async () => {
    const testId = null;
    const res = await redeemOneFreeCredit(testId);
    if (res.success) {
      setShowFreeConfirm(false);
      navigate(`/online-practice-test/${slug}`, { state: { type: 'category-test', freeAttempt: true, quesNumber: categoryInfo.examOutline.totalQuestions} });
    } else {
      // keep banner open and show error
    }
  };

  // Called when user cancels the free attempt
  const cancelUseFree = () => {
    setShowFreeConfirm(false);
    setRedeemError(null);
  };

  const startTopic = async (subSlug) => {
    const ok = await ensureAccess();
    if (ok) {
      navigate(`/practice-test-exam-prep/${slug}/topic/${subSlug}/start`, {
        state: { type: 'subcategory-test' },
      });
    }
  };

  // ===== Pricing CTA handler =====
  const handleSubscribeClick = () => {
    if (!isLoggedIn) {
      goLogin('subscribe');
      return;
    }
    navigate(`/subscribe-practice-test/${slug}`, {
      state: { categoryName: slug.replaceAll('-', ' ') },
    });
  };

  // Close modal on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && showFreeConfirm && !isRedeeming) {
        cancelUseFree();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showFreeConfirm, isRedeeming]);

  return (
    <>
      <MetaTags>
        <title>
          {categoryInfo?.seo?.metaTitle || `${slug.replaceAll('-', ' ')} Online Practice | PolyQuizz`}
        </title>
        <meta
          name="description"
          content={categoryInfo?.seo?.metaDescription || categoryInfo?.description || ''}
        />
      </MetaTags>

      

      {/* ===================== HERO ===================== */}
      {categoryInfo && (
        <main className="cat-hero">
          <div className="cat-hero-inner">
            <div className="cat-badge">{categoryInfo.hero?.badge || 'Practice Ready'}</div>
            {isSubscribed && (
            <div className="cat-badge">Subscribed</div>
            )}
            <h1 className="cat-title">{categoryInfo.title || slug.replaceAll('-', ' ')}</h1>
            {Array.isArray(categoryInfo.description) ? (
  categoryInfo.description.map((para, i) => (
    <p className="cat-desc" key={i}>{para}</p>
  ))
) : (
  <p className="cat-desc">{categoryInfo.description}</p>
)}

            {categoryInfo.hero?.stats && (
              <div className="cat-stats">
                {categoryInfo.hero.stats.totalQuestions && (
                  <span>📝 {categoryInfo.hero.stats.totalQuestions} Questions</span>
                )}
                {categoryInfo.hero.stats.timeLimit && (
                  <span>⏱️ {categoryInfo.hero.stats.timeLimit}</span>
                )}
                {/* {categoryInfo.hero.stats.difficulty && (
                  <span>🎯 {categoryInfo.hero.stats.difficulty}</span>
                )} */}
              </div>
            )}

            <div className="cat-cta">
              {isLoggedIn ? (
                <button className="primary-btn-lg" onClick={startFull} disabled={isFetchingCredits || isRedeeming}>
                  Start {categoryInfo.title || slug.replaceAll('-', ' ')}
                </button>
              ) : (
                <button className="primary-btn-lg btn-outline" onClick={() => navigate('/register')}>
                  Sign Up to Subscribe and Start Practice
                </button>   
              )}
              {/* New free trial info line */}
  {!isLoggedIn && (
    <div className="free-trial-note">
      <span className="gift-icon">🎁</span>
      Get <strong>2 free practice tests</strong> instantly when you sign up!
    </div>
  )}
            </div>
            

            {/* ===================== FREE-CREDIT CONFIRMATION MODAL ===================== */}
            {showFreeConfirm && (
              <div className="pq-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="pq-modal-title">
                <div className="pq-modal-card" role="document">
                  <button
                    className="pq-modal-close"
                    aria-label="Close"
                    onClick={cancelUseFree}
                    disabled={isRedeeming}
                  >
                    ×
                  </button>

                  <div className="pq-modal-body">
                    <div className="pq-modal-hero">
                      <svg width="56" height="56" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect width="24" height="24" rx="12" fill="#EEF2FF"/>
                        <path d="M8 12l2.5 2L16 9" stroke="#4F46E5" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>

                    <h2 id="pq-modal-title" className="pq-modal-title">
                      Use a free practice attempt
                    </h2>

                    <p className="pq-modal-sub">
                      {isFetchingCredits ? (
                        'Checking your free attempts…'
                      ) : (
                        <>
                          {freeCredits === 2 ? (
                            <>This will be your <strong>1st</strong> of 2 free full practice tests.</>
                          ) : freeCredits === 1 ? (
                            <>This will be your <strong>2nd</strong> of 2 free full practice tests.</>
                          ) : (
                            <>You have a free attempt available.</>
                          )}
                        </>
                      )}
                    </p>

                    <div className="pq-modal-meta">
                      <div className="pq-meta-item">
                        <div className="pq-meta-label">Category</div>
                        <div className="pq-meta-value">{slug.replaceAll('-', ' ').toUpperCase()}</div>
                      </div>
                      <div className="pq-meta-item">
                        <div className="pq-meta-label">Remaining</div>
                        <div className="pq-meta-value">{freeCredits}</div>
                      </div>
                    </div>

                    {redeemError && <div className="pq-modal-error" role="alert">{redeemError}</div>}

                    <div className="pq-modal-actions">
                      <button
                        className="btn btn-secondary pq-btn-wide"
                        onClick={cancelUseFree}
                        disabled={isRedeeming}
                      >
                        Cancel
                      </button>

                      <button
                        className="btn btn-primary pq-btn-wide"
                        onClick={confirmUseFree}
                        disabled={isRedeeming}
                      >
                        {isRedeeming ? 'Starting…' : 'Use Free Attempt'}
                      </button>
                    </div>

                    <div className="pq-modal-foot">
                      <small>Free attempts are limited. After using them, you'll need to subscribe to continue full practice.</small>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      )}

      {/* ===================== FEATURES / ADVANTAGES (from JSON) ===================== */}
      {categoryInfo?.advantages?.items?.length ? (
        <section className="feats-wrap">
          <div className="feats-inner">
            <h2 className="feats-title">
              {categoryInfo.advantages.heading || 'Key Features & Benefits'}
            </h2>

            {categoryInfo.advantages.subheading ? (
              <p className="feats-sub">{categoryInfo.advantages.subheading}</p>
            ) : null}

            <div className="feats-grid">
              {categoryInfo.advantages.items.slice(0, 12).map((f) => (
                <article key={f.title} className="feat-card">
                  <div className="feat-icon">
                    {f.icon ? <span aria-hidden>{f.icon}</span> : <span aria-hidden>⭐</span>}
                  </div>

                  <h3 className="feat-h3">{f.title}</h3>
                  {f.desc && <p className="feat-desc">{f.desc}</p>}
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ===================== TOPICS COVERAGE OVERVIEW (from API) ===================== */}
      {subCategories.length > 0 && (
        <section className="coverage-wrap">
          <div className="coverage-inner">
            <h2 className="feats-title">
              Topics Covered in {categoryInfo?.title || slug.replaceAll('-', ' ')}
            </h2>
            <p className="coverage-sub">
              This exam includes questions from multiple sections. You can take a single 
              full-length practice test that combines all of the topics listed below, 
              or choose to practice individual sections separately for focused preparation.
            </p>

            <div className="coverage-pills" aria-label="Covered topics">
              {subCategories.map((sc) => (
                <span key={sc.SubCatName} className="pill">
                  <span className="pill-dot" />
                  {sc.SubCatName}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

     {/* ===================== PRICING SECTION (only if not subscribed) ===================== */}
{!isSubscribed && categoryInfo?.pricing && (
  <section className="pricing-wrap" id="pricing-section">
    <h2 className="cat-h2">Subscribe This Exam</h2>
    <p className="cat-p">
      Get access to all topic-wise tests, full-length mocks, detailed solutions,
      and performance analytics. Pick a duration that fits your study schedule.
    </p>

    <div className="pricing-grid" style={{ marginTop: 18 }}>
      {Array.isArray(categoryInfo.pricing.tiers) && categoryInfo.pricing.tiers.length > 0 ? (
        categoryInfo.pricing.tiers.map((plan) => {
          const currencySymbol = categoryInfo.pricing.currency === 'USD' ? '$' : '₹';
          const recommended = !!plan.recommended;
          return (
            <article
              key={plan.id}
              className={`price-card ${plan.best ? 'best' : ''} ${recommended ? 'recommended' : ''}`}
              style={{ position: 'relative' }}
            >
              {plan.badge && <span className="pc-badge">{plan.badge}</span>}
              {recommended && !plan.badge && <span className="pc-badge">Recommended</span>}

              <h3 className="pc-title">{plan.name}</h3>

              <div className="pc-price">
                <span className="amount">
                  {currencySymbol}{plan.price}
                </span>
                <span className="per">{plan.billing || (plan.months ? `one time (${plan.months} month${plan.months>1?'s':''})` : '')}</span>
              </div>

              <ul className="pc-list">
                {(plan.includes || []).map((it, i) => (
                  <li key={i}>
                    <span className="dot" />
                    {it}
                  </li>
                ))}
              </ul>

              <div style={{ marginTop: 8 }}>
                <button
                  className="primary-btn-lg"
                  onClick={() => {
                    if (!isLoggedIn) {
                      goLogin('subscribe');
                      return;
                    }

console.log({
    category: slug,
    price: plan.price,
    currency: categoryInfo.pricing.currency,
    plan
});

                    // navigate to subscribe page with selected plan info in state
                    navigate(`/subscribe-practice-test/${slug}`, {
                      state: {
                        userId: getLoggedInUser(),
                        categoryName: slug.replaceAll('-', ' '),
                        planId: plan.id,
                        planMonths: plan.months ?? (plan.billing && plan.billing.includes('month') ? 1 : null),
                        planPrice: plan.price,
                        planName: plan.name
                      }
                    });
                  }}
                >
                  Subscribe
                </button>
              </div>
            </article>
          );
        })
      ) : (
        <div style={{ padding: 12 }}>
          Pricing is not available for this category. Please contact support for details.
        </div>
      )}
    </div>

    {/* <p className="pricing-note" style={{ marginTop: 12 }}>
      All tiers include the same features — duration differs only. You can change or cancel anytime.
    </p> */}
  </section>
)}


{/* ===================== TOPICS / SUBCATEGORIES FROM API ===================== */}

{subCategories.length > 0 && isLoggedIn && isSubscribed && (
  <section className="topics-wrap">
    <h2>Topic-wise Practice</h2>
    <p className="topics-subtext">
      Strengthen your understanding by practicing chapter-wise and topic-specific tests.  
      Each topic test helps you focus on your weak areas, improve accuracy, and build confidence 
      before attempting the full-length mock exams.
    </p>

    <SubCatCard allSubCategories={subCategories} onStartTopic={startTopic} />
  </section>
)}




         {/* ===================== INTRO SECTION ===================== */}
      {categoryInfo?.intro && (
        <section className="cat-section">
          <h2 className="cat-h2">About {categoryInfo.title}</h2>
          {categoryInfo.intro.blurb && <p className="cat-p">{categoryInfo.intro.blurb}</p>}
          {categoryInfo.intro.ctaNote && <p className="cat-p">{categoryInfo.intro.ctaNote}</p>}

          {Array.isArray(categoryInfo.intro.resources) && (
            <ul className="res-list">
              {categoryInfo.intro.resources.map((r) => (
                <li key={r.label}>
                  <a href={r.url} className="res-link" target="_blank" rel="noreferrer">
                    {r.label}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ===================== ELIGIBILITY SECTION ===================== */}
      {categoryInfo?.eligibility && (
        <section className="cat-section">
          <h2 className="cat-h2">Exam Eligibility</h2>
          {categoryInfo.eligibility.license && (
            <p className="cat-p">
              License required: <strong>{categoryInfo.eligibility.license}</strong>
            </p>
          )}
          {categoryInfo.eligibility.requirementsTitle && (
            <p className="cat-p">{categoryInfo.eligibility.requirementsTitle}</p>
          )}
          {Array.isArray(categoryInfo.eligibility.requirements) && (
            <ul className="bullet-list">
              {categoryInfo.eligibility.requirements.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ===================== EXAM OUTLINE SECTION ===================== */}
      {categoryInfo?.examOutline && (
        <section className="cat-section">
          <h2 className="cat-h2">{categoryInfo.title} Exam Outline</h2>
          {categoryInfo.examOutline.summary && (
            <p className="cat-p">{categoryInfo.examOutline.summary}</p>
          )}

          <div className="outline-stats">
            {categoryInfo.examOutline.totalQuestions && (
              <div className="o-stat">📝 {categoryInfo.examOutline.totalQuestions} Qs</div>
            )}
            {categoryInfo.examOutline.unscored && (
              <div className="o-stat">🧪 {categoryInfo.examOutline.unscored} Unscored</div>
            )}
            {categoryInfo.examOutline.timeLimit && (
              <div className="o-stat">⏱️ {categoryInfo.examOutline.timeLimit}</div>
            )}
          </div>

          {Array.isArray(categoryInfo.examOutline.sections) && (
            <div className="outline-sections">
              {categoryInfo.examOutline.sections.map((sec) => (
                <article key={sec.title} className="outline-card">
                  <header className="outline-head">
                    <h3>{sec.title}</h3>
                    {sec.weight && <span className="badge">{sec.weight}</span>}
                  </header>
                  {Array.isArray(sec.domains) && (
                    <ul className="domain-list">
                      {sec.domains.map((d) => (
                        <li key={d.name}>
                          <span className="dot" />
                          <span className="name">{d.name}</span>
                          {d.weight && <span className="w">{d.weight}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      

  
    </>
  );
}
